import {
  capabilityResourceGroupMap,
  resouceGroupRouteMap,
  whiteListedCapabilities,
} from '@/router/constant';
import { AuthService } from '@/services/api/auth.service';
import { Permissions, Service } from '@/utils/types/global';
import { useGetters, useDispatch } from '@/utils/useMappers';
import { ComputedRef } from 'vue';
import { permissionTypes, resourceGroupRouteList, NOTIFICATION_RESOURCE_GROUP } from '@/router/constant';

export function usePermissions() {
  const [permissions] = useGetters(['globalState/app/getPermissions']) as [
    ComputedRef<Permissions>,
  ];
  const [setPermissions] = useDispatch(['globalState/app/setPermissions']);

  const fetchPermissions = async () => {
    setPermissions((await AuthService.getPermissions()) as Permissions);
    return (await AuthService.getPermissions()) as Permissions;
  };

  const hasCurrentServiceWritePermission = (service: Service): boolean => {
    return service?.permissions?.includes('WRITE') ?? false;
  };

  const hasCapabilityPermissionByRoute = (capability: string) => {
    const isAlertActive = resourceGroupRouteList.some(
      (item) =>
        permissions?.value?.[NOTIFICATION_RESOURCE_GROUP] ||
        hasCapabilityAlertPermission(item.resourceGroup),
    );
    return (
      !!permissions?.value?.[resouceGroupRouteMap[capability]]?.READ ||
      !!permissions?.value?.[resouceGroupRouteMap[capability]]?.WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.READ ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.WRITE ||
      (isAlertActive && whiteListedCapabilities.includes(capability))
    );
  };

  const hasCapabilityWritePermissionByRoute = (capability: string) => {
    return (
      !!permissions?.value?.[resouceGroupRouteMap[capability]]?.WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.WRITE ||
      whiteListedCapabilities.includes(capability)
    );
  };

  const hasCapabilityPermission = (capability: string) => {
    return (
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]?.READ ||
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]?.WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]
    );
  };

  const hasCapabilityWritePermission = (capability: string) => {
    return (
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]?.WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.WRITE ||
      false
    );
  };

  const hasCapabilityReadPermission = (capability: string) => {
    return (
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]?.READ ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.READ ||
      false
    );
  };

  const getPermissions = async () => {
    if (!permissions?.value) {
      await fetchPermissions();
    }

    return permissions.value;
  };

  const fetchServicePermissions = async (
    serviceAccessList: Array<{
      resource: string;
      resourceGroup: string;
      permission?: string;
    }>,
  ) => {
    const updatedServiceAccessList = serviceAccessList.map((item) => ({
      ...item,
      permission: item.permission || permissionTypes.READ, // Default to 'READ' if permission is not provided
    }));

    return AuthService.fetchServicePermissions(updatedServiceAccessList);
  };

  const hasCurrentServiceAlertWritePermission = (service: Service): boolean => {
    return service?.permissions?.includes('ALERT_WRITE') ?? false;
  };
  const hasCapabilityAlertWritePermission = (capability: string) => {
    return (
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]
        ?.ALERT_WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG]?.WRITE ||
      false
    );
  };

  const hasCapabilityAlertPermission = (capability: string) => {
    return (
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]
        ?.ALERT_READ ||
      !!permissions?.value?.[capabilityResourceGroupMap[capability]]
        ?.ALERT_WRITE ||
      !!permissions?.value?.[capabilityResourceGroupMap.CONFIG] ||
      false
    );
  };

  return {
    permissions,
    hasCurrentServiceWritePermission,
    fetchPermissions,
    getPermissions,
    hasCapabilityPermission,
    hasCapabilityWritePermission,
    hasCapabilityReadPermission,
    hasCapabilityPermissionByRoute,
    fetchServicePermissions,
    hasCurrentServiceAlertWritePermission,
    hasCapabilityAlertWritePermission,
    hasCapabilityWritePermissionByRoute,
    hasCapabilityAlertPermission,
  };
}
